import { useRef, useState } from 'react';
import { last } from 'lodash';
import { findFirstOverflowedSlideFromLeft, findFirstOverflowedSlideFromRight, findSecondVisibleSlideFromTheLeft, setCanScrollByPosition } from '../../utils';
import { DIRECTIONS, SCROLL_TYPE, TRANSITION_MS } from '../../constants';
import { useScrollCounter } from '../useScrollCounter';

export const useScroll = ({
    scrollType,
    isInfinite,
    slides,
    slidesToShow,
    withSlidesIndicator = false,
    swipeMode = false,
    onArrowClick = Function.prototype,
    showSlidesIndicatorInSwipeMode = false
}) => {
    const slidesTrackRef = useRef(null);
    const carouselTransformValue = useRef(0);
    const slidesListRef = useRef(null);
    const scrolledSlidesStack = useRef([]);

    const [canScrollRight, setCanScrollRight] = useState(slides.length > slidesToShow);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    const {
        currentPosition,
        supportSlidesIndicator,
        resetCounterPosition,
        changeCounterPosition
    } = useScrollCounter({ scrollType, slidesToShow, withSlidesIndicator, swipeMode, showSlidesIndicatorInSwipeMode });

    const scrollToPosition = (position) => {
        const slides = slidesTrackRef.current.children;

        if (!slides[position] || !shouldScroll()) {
            return;
        }

        const slideRect = slides[position].getBoundingClientRect();

        const slidesListPosition = slidesListRef.current.getBoundingClientRect();
        const slidesTrackPosition = slidesTrackRef.current.getBoundingClientRect();
        const scrolledAmount = scrollToFirstVisibleSlide({ slideRect, slidesListPosition, slidesTrackPosition });

        if (scrolledAmount !== 0) {
            beforeScroll();
            checkTrackPosition();
            setTransformValue();
            changeCounterPosition(position);
        }
    };

    /**
     * Called when clicking on the right arrow.
     * First checks if the prev move was left - if so takes the value from the stack.
     * If not calculates how much to move by calling dragSlideByType.
     */
    const scrollRight = () => {
        if (!shouldScroll()) {
            return;
        }

        beforeScroll();

        const { left } = last(scrolledSlidesStack.current) || {};
        const rightEdgeBack = isInfinite && !canScrollRight;

        rightEdgeBack ? changeCounterPosition(0) : changeCounterPosition(currentPosition + 1);

        if (left) {
            carouselTransformValue.current = carouselTransformValue.current + left;
            scrolledSlidesStack.current.pop();
        } else {
            const slidesListPosition = slidesListRef.current.getBoundingClientRect();
            const slidesTrackPosition = slidesTrackRef.current.getBoundingClientRect();
            const slides = slidesTrackRef.current.children;

            if (rightEdgeBack) {
                const firstSlide = slides[0].getBoundingClientRect();
                scrollToLastVisibleSlide({ slideRect: firstSlide, slidesListPosition, slidesTrackPosition, direction: DIRECTIONS.RIGHT });
            } else {
                dragSlideByType[scrollType]({ slidesListPosition, slidesTrackPosition, slides, direction: DIRECTIONS.RIGHT });
            }
        }

        checkTrackPosition();
        setTransformValue();

        onArrowClick({
            direction: DIRECTIONS.RIGHT
        });
    };

    /**
     * Called when clicking on the left arrow.
     * First checks if the prev move was right - if so takes the value from the stack.
     * If not calculates how much to move by calling dragSlideByType.
     */
    const scrollLeft = () => {
        if (!shouldScroll()) {
            return;
        }

        beforeScroll();

        const { right } = last(scrolledSlidesStack.current) || {};
        const leftEdgeBack = isInfinite && !canScrollLeft;

        leftEdgeBack ? changeCounterPosition(slides.length - 1) : changeCounterPosition(currentPosition - 1);

        if (right) {
            carouselTransformValue.current = carouselTransformValue.current + right;
            scrolledSlidesStack.current.pop();
        } else {
            const slidesListPosition = slidesListRef.current.getBoundingClientRect();
            const slidesTrackPosition = slidesTrackRef.current.getBoundingClientRect();
            const slides = slidesTrackRef.current.children;

            if (leftEdgeBack) {
                const lastSlide = slides[slides.length - 1].getBoundingClientRect();
                scrollToLastVisibleSlide({ slideRect: lastSlide, slidesListPosition, slidesTrackPosition, direction: DIRECTIONS.LEFT });
            } else {
                dragSlideByType[scrollType]({ slidesListPosition, slidesTrackPosition, slides, direction: DIRECTIONS.LEFT });
            }
        }

        checkTrackPosition();
        setTransformValue();

        onArrowClick({
            direction: DIRECTIONS.LEFT
        });
    };

    /**
     * Scroll type mapper
     * handleSingleScroll is called when the scrollType is single.
     * handleRowScroll is called when the scrollType is row.
     */
    const dragSlideByType = {
        [SCROLL_TYPE.SINGLE]: ({ slidesListPosition, slidesTrackPosition, slides, direction }) => {
            handleSingleScroll({ slidesListPosition, slidesTrackPosition, slides })[direction]();
        },
        [SCROLL_TYPE.ROW]: ({ slidesListPosition, slidesTrackPosition, slides, direction }) => {
            handleRowScroll({ slidesListPosition, slidesTrackPosition, slides })[direction]();
        }
    };

    /**
     * Set new carouselTransformValue and update the scrolledSlidesStack.
     * @param {number} amountToMove The amount to reduce from the current transform value.
     * @param {string} direction Clicked arrow direction, can be either left or right.
     */
    const moveSlide = ({ amountToMove, direction }) => {
        carouselTransformValue.current = carouselTransformValue.current - amountToMove;

        if (direction) {
            scrolledSlidesStack.current.push({ [direction]: amountToMove });
        }
    };

    /**
     * Change the carousel transform value by dragging a slide to the most left point of the carousel container.
     * @param {Node} slideRect The slide we want to drag.
     * @param {object} slidesListPosition The BoundingClientRect of the slides list element.
     * @param {object} slidesTrackPosition The BoundingClientRect of the slides track element.
     * @param {string} direction Clicked arrow direction, can be either left or right.
     */
    const scrollToFirstVisibleSlide = ({ slideRect, slidesListPosition, slidesTrackPosition, direction }) => {
        let amountToMove = slideRect.left - slidesListPosition.left;

        const isLastScroll = slidesTrackRef.current.scrollWidth + carouselTransformValue.current - amountToMove - slidesTrackPosition.width < 1;

        if (isLastScroll) {
            amountToMove = carouselTransformValue.current + (slidesTrackRef.current.scrollWidth - slidesTrackPosition.width);
        }

        moveSlide({ amountToMove, direction });

        return amountToMove;
    };

    /**
     * Change the carousel transform value by dragging a slide to the most right point of the carousel container.
     * @param {Node} slideRect The slide we want to drag.
     * @param {object} slidesListPosition The BoundingClientRect of the slides list element.
     * @param {object} slidesTrackPosition The BoundingClientRect of the slides track element.
     * @param {string} direction Clicked arrow direction, can be either left or right.
     */
    const scrollToLastVisibleSlide = ({ slideRect, slidesListPosition, slidesTrackPosition, direction }) => {
        let amountToMove = slideRect.right - slidesListPosition.right;

        const isLastScroll = slidesTrackPosition.left - amountToMove >= 0;

        if (isLastScroll) {
            amountToMove = carouselTransformValue.current;
        }

        moveSlide({ carouselTransformValue, amountToMove, direction });
    };

    /**
     * Move one slide.
     * @param {object} slidesListPosition The BoundingClientRect of the slides list element.
     * @param {object} slidesTrackPosition The BoundingClientRect of the slides track element.
     * @param {NodeList} slides List of all the slides.
     */
    const handleSingleScroll = ({ slidesListPosition, slidesTrackPosition, slides }) => ({
        [DIRECTIONS.LEFT]: () => {
            const overflowedFromLeft = findFirstOverflowedSlideFromLeft(slidesListPosition, slides);
            overflowedFromLeft && scrollToFirstVisibleSlide({ slideRect: overflowedFromLeft, slidesListPosition, slidesTrackPosition, direction: DIRECTIONS.LEFT });
        },
        [DIRECTIONS.RIGHT]: () => {
            const secondVisibleSlide = findSecondVisibleSlideFromTheLeft(slidesListPosition, slides);
            secondVisibleSlide && scrollToFirstVisibleSlide({ slideRect: secondVisibleSlide, slidesListPosition, slidesTrackPosition, direction: DIRECTIONS.RIGHT });
        }
    });

    /**
     * Move full row - the amount of visible slides.
     * @param {object} slidesListPosition The BoundingClientRect of the slides list element.
     * @param {object} slidesTrackPosition The BoundingClientRect of the slides track element.
     * @param {NodeList} slides List of all the slides.
     */
    const handleRowScroll = ({ slidesListPosition, slidesTrackPosition, slides }) => ({
        [DIRECTIONS.LEFT]: () => {
            const overflowedFromLeft = findFirstOverflowedSlideFromLeft(slidesListPosition, slides);
            overflowedFromLeft && scrollToLastVisibleSlide({ slideRect: overflowedFromLeft, slidesListPosition, slidesTrackPosition, direction: DIRECTIONS.LEFT });
        },
        [DIRECTIONS.RIGHT]: () => {
            const overflowedFromRight = findFirstOverflowedSlideFromRight(slidesListPosition, slides);
            overflowedFromRight && scrollToFirstVisibleSlide({ slideRect: overflowedFromRight, slidesListPosition, slidesTrackPosition, direction: DIRECTIONS.RIGHT });
        }
    });

    /**
     * Check if able to scroll
     * If isAnimating is true abort scroll.
     */
    const shouldScroll = () => {
        if (isAnimating) {
            return false;
        }
        return true;
    };

    /**
     * Called before scrolling
     * Set isAnimating to true and add transition attribute.
     */
    const beforeScroll = () => {
        setIsAnimating(true);
        addTransition();
    };

    /**
     * Adding the transition before scrolling.
     */
    const addTransition = () => {
        slidesTrackRef.current.style.transition = `transform ${TRANSITION_MS}ms ease`;
    };

    /**
     * Remove the transition after scrolling.
     */
    const onTransitionEnd = ({ target }) => {
        if (target === slidesTrackRef.current) {
            slidesTrackRef.current.style.removeProperty('transition');
            setIsAnimating(false);
        }
    };

    /**
     * Setting transform value to the carousel so it will move.
     */
    const setTransformValue = () => {
        slidesTrackRef.current.style.transform = `translate3d(${carouselTransformValue.current}px, 0, 0)`;
    };

    /**
     * Check the track position to know if there are more slides on either left or right point.
     */
    const checkTrackPosition = () => {
        const trackRightPosition = slidesTrackRef.current.offsetWidth - slidesTrackRef.current.scrollWidth;

        const isTrackOnRightPosition = Math.floor(carouselTransformValue.current) <= trackRightPosition;
        const isTrackOnLeftPosition = carouselTransformValue.current === 0;

        setCanScrollByPosition(isTrackOnLeftPosition, setCanScrollLeft);
        setCanScrollByPosition(isTrackOnRightPosition, setCanScrollRight);
    };

    /**
     * Reset carousel props to default.
     */
    const resetCarousel = () => {
        scrolledSlidesStack.current = [];
        carouselTransformValue.current = 0;
        setTransformValue();
        setCanScrollRight(slides.length > slidesToShow);
        setCanScrollLeft(false);
        checkTrackPosition();
        resetCounterPosition();
    };

    /**
     * Update the Counter Position when user scroll the carousel by swipe instead of by clicking on the arrows
     * (it's important to keep the Counter Position up to date in order to update the dots' indicator)
     * */
    const updateCounterPositionOnScrollBySwipe = ({ target: listContainer }) => {
        const newPosition = searchItemInContainer(listContainer, currentPosition);
        if (newPosition !== currentPosition) {
            changeCounterPosition(newPosition);
        }
    };

    const searchItemInContainer = (listContainer, index) => {
        const boundary = listContainer.getBoundingClientRect();
        const { left, right } = listContainer.children[index].getBoundingClientRect();
        const swipeToLeft = left > boundary.right;
        const swipeToRight = right < boundary.left;

        if (swipeToLeft) {
            const nextIndex = index - 1;
            if (nextIndex >= 0) {
                return searchItemInContainer(listContainer, nextIndex);
            }
        }
        if (swipeToRight) {
            const nextIndex = index + 1;
            if (nextIndex < listContainer.childElementCount) {
                return searchItemInContainer(listContainer, nextIndex);
            }
        }

        return index;
    };

    return ({
        scrollRight,
        scrollLeft,
        scrollToPosition,
        onTransitionEnd,
        resetCarousel,
        scrollToFirstVisibleSlide,
        scrollToLastVisibleSlide,
        setTransformValue,
        checkTrackPosition,
        updateCounterPositionOnScrollBySwipe,
        carouselTransformValue,
        scrolledSlidesStack,
        slidesTrackRef,
        canScrollLeft,
        slidesListRef,
        canScrollRight,
        currentPosition,
        supportSlidesIndicator
    });
};
